<template>
    <div class="w-full flex flex-col items-start relative">
        <div class="w-full mt-1 flex flex-row items-center">
            <div :class="config.description && !hideDesc ? 'flex-1 mr-4' : 'grow'">
                <label :for="config.display_name" class="block text-sm font-medium text-gray-700" :class="overrideLabelOpacity ? 'opacity-50' : ''">
                    {{ config.display_name }}
                </label>

                <div class="mt-1 w-full border border-gray-300 rounded-md bg-white" :style="{ height: editorHeight }">
                <VueMonacoEditor
                        :key="editorUid"
                        v-model:value="code"
                        :language="activeLangId"
                        :theme="effectiveThemeId"
                        :path="editorPath"
                        :options="editorOptions"
                        @beforeMount="onEditorBeforeMount"
                        @mount="onEditorMount"
                        class="w-full h-full" />
                </div>

                <div v-if="errors.length" class="text-red-700 mt-1 text-xs">
                    <template v-for="err in errors" :key="err.$uid">
                        <span>· {{ err.$message }}</span
                        ><br />
                    </template>
                </div>
            </div>

            <!-- lateral description -->
            <div v-if="showTooltip" class="ml-2">
                <Tooltip :tooltip-text="config.description" position="left" />
            </div>
            <div
                v-if="showSideDesc"
                class="flex-1 p-3 bg-green-50 border-l-4 border-green-200 text-xs text-black rounded-md relative arrow-left"
                :class="errors.length ? 'mb-5' : ''">
                <span v-html="config.description"></span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import Tooltip from './Tooltip.vue';
import _ from 'lodash';

// Predefined custom languages
const PREDEFINED_LANGS = {
    dbpath: {
        id: 'dbpath',
        monarch: {
            defaultToken: '',
            tokenizer: {
                root: [
                    [/[A-Za-z_]\w*/, 'source'],
                    [/\./, 'delimiter'],
                    [/:/, { token: 'colon', next: '@destination' }]
                ],
                destination: [
                    [/[A-Za-z_]\w*/, 'dest'],
                    [/\./, 'delimiter']
                ]
            }
        },
        themeRules: [
            { token: 'source', foreground: '008800', fontStyle: 'bold' },
            { token: 'dest', foreground: '000088', fontStyle: 'bold' },
            { token: 'delimiter', foreground: '444444' },
            { token: 'colon', foreground: '880000', fontStyle: 'italic' }
        ]
    },
    network: {
        id: 'network',
        monarch: { defaultToken: '', tokenizer: { root: [[/(?:\d{1,3}\.){3}\d{1,3}:\d+/, 'ipPort']] } },
        themeRules: [{ token: 'ipPort', foreground: '880000', fontStyle: 'italic' }]
    },
    pathmap: {
        id: 'pathmap',
        monarch: { defaultToken: '', tokenizer: { root: [[/[A-Za-z_]\w*=[A-Za-z_]\w*/, 'keyVal']] } },
        themeRules: [{ token: 'keyVal', foreground: '444400' }]
    },
    sqlref: {
        id: 'sqlref',
        monarch: {
            defaultToken: '',
            tokenizer: { root: [[/[A-Za-z_]\w*\.[A-Za-z_]\w*/, 'tableCol']] }
        },
        themeRules: [{ token: 'tableCol', foreground: '0055AA', fontStyle: 'underline' }]
    }
};

// Props & emits
const props = defineProps({
    config: { type: Object, required: true },
    value: { type: [String, null], default: null },
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: { type: Boolean, default: false },
    hideLateralDescription: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] }
});
const emit = defineEmits(['input']);

// Editor content
const code = ref(_.isString(props.value) ? props.value : props.config.value.default || '');
watch(
    () => props.value,
    (v) => (code.value = v)
);
watch(code, (v) => emit('input', v));

// Sidebar visibility
const hideDesc = ref(props.hideLateralDescription);
const onResize = () => {
    hideDesc.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
};
onMounted(() => {
    onResize();
    window.addEventListener('resize', onResize);
});
onUnmounted(() => window.removeEventListener('resize', onResize));

// Height
const DEFAULT_LINES = 10;
const LINE_HEIGHT_PX = 18;
const editorHeight = computed(() => {
    const v = props.config.value;
    if (v.heightPx != null) return v.heightPx + 'px';
    const lines = v.heightLines || DEFAULT_LINES;
    return lines * LINE_HEIGHT_PX + 'px';
});

// Configuration
const cfg = props.config.value;
// ensure unique model path and component key so multiple editors don't share models
const editorUid = _.uniqueId('codeEditor_');
const editorPath = computed(() => (cfg.path ? `${cfg.path}_${editorUid}` : editorUid));
let custom = cfg.customLangDefinition || (cfg.customLangKey && PREDEFINED_LANGS[cfg.customLangKey]);
const activeLangId = custom?.id || cfg.language || 'javascript';
const effectiveThemeId = custom?.themeRules ? `${custom.id}-theme` : cfg.theme || 'vs';
const editorOptions = cfg.options || {};

// Validators
const validators = [];
if (typeof cfg.validatorDefinition === 'function') validators.push(cfg.validatorDefinition);
if (cfg.validation?.regex) {
    const { regex: pattern, flags = '', error_message: errorMessage = 'Invalid format.', severity = monaco.MarkerSeverity.Error } = cfg.validation;
    let rx;
    try {
        rx = new RegExp(pattern, flags);
    } catch {
        rx = null;
    }
    if (rx) {
        validators.push((codeVal) =>
            rx.test(codeVal) ? [] : [{ severity, message: errorMessage, startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 }]
        );
    }
}

// Before Monaco initialises: register custom languages & themes
function onEditorBeforeMount(monacoInstance) {
    if (!custom) return;
    monacoInstance.languages.register({ id: custom.id });
    monacoInstance.languages.setMonarchTokensProvider(custom.id, custom.monarch);
    if (custom.themeRules) {
        monacoInstance.editor.defineTheme(`${custom.id}-theme`, {
            base: cfg.theme || 'vs',
            inherit: true,
            rules: custom.themeRules,
            colors: {}
        });
    }
}

// After mount: set language & apply diagnostics
function onEditorMount(editor, monacoInstance) {
    if (custom && editor.getModel()) {
        monacoInstance.editor.setModelLanguage(editor.getModel(), custom.id);
    }
    if (validators.length) {
        const runValidators = () => {
            let markers = [];
            for (const fn of validators) markers = markers.concat(fn(editor.getValue()) || []);
            monacoInstance.editor.setModelMarkers(editor.getModel(), activeLangId, markers);
        };
        editor.onDidChangeModelContent(runValidators);
        runValidators();
    }
}

function onEditorChange(val) {
    // Additional change logic if required
}

// example of mocked json for testing
// const mockedJson = ref({
//     config: {
//         display_name: 'SQL Mapping',
//         description: 'Write your `schema.table.column:dest.schema.table` mappings here.',
//         value: {
//             path: 'mappings/config.txt',
//             language: 'plaintext',
//             theme: 'vs',
//             options: {
//                 wordWrap: 'on',
//                 minimap: { enabled: false }
//             },
//             default: 'public.users.id:archive.users.user_id',
//             heightLines: 8,
//             customLangKey: 'dbpath',
//             validation: {
//                 regex: '^[A-Za-z0-9_]+(?:\\.[A-Za-z0-9_]+){1,2}(?::[A-Za-z0-9_]+(?:\\.[A-Za-z0-9_]+){1,2})?$',
//                 flags: 'm',
//                 error_message: 'Must be `schema.table.column[:schema.table.column]` format',
//                 severity: 8
//             }
//         },
//         readonly: false
//     },
//     value: 'public.orders.amount:archive.orders.total_amount'
// });
</script>

<style scoped>
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-right: 12px solid #bbf7d0;
}
</style>
